import React, { useEffect, useState, useRef } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { osName, OsTypes } from 'react-device-detect';
import braintree from 'braintree-web';
import CardForm from './components/CardForm';
import styled from 'styled-components';
import useDebounce from '../../helper/useDebounce';
import {braintreePurchase, setFormValidity} from './checkoutSlice';
import {selectAnalyticsData, selectEventsData, setEventData, setEventDataList} from '../events/eventsSlice';
import { sendAnalyticsEvents } from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {selectPlan, selectShowCheckout} from '../plans/plansSlice';
import {selectToken, selectUserUuid} from '../signup/signupSlice';
import {setCheckoutLoader} from '../loader/loaderSlice';
import { tabletMinWidth } from '../../mixins';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';


const GOOGLE_PAY_MERCHANT_ID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID;

const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const ApplePayButton = styled.div`
  width: 90%;
  max-width: 327px;
  margin: 20px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  //margin-bottom: 24px;
  background: #000 url(${'../assets/applePay-logo.svg'}) center / contain no-repeat;
  background-origin: content-box;
  border-radius: 12px;
  padding: 16px 34px;
  
  @media screen and (min-width: ${tabletMinWidth}px) {
   cursor: pointer; 
  }
`;

const GooglePayButton = styled.div`
  width: 90%;
  max-width: 327px;
  margin: 20px auto 0;
  height: 56px;
  //margin-bottom: 24px;
  
  @media screen and (min-width: ${tabletMinWidth}px) {
   cursor: pointer; 
  }
  
  .gpay-button.black.hover {
    background-color: #000000;
  }
`;

const Braintree = ({ btToken, braintreeInstance, cardInstance, threeDSInstance, card, expiration, cvc, cardholder, renderBrand, planDetails }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentsClient = useRef();
  const googlePayInstance = useRef();
  const applePayInstance = useRef();
  const [cardBrand, setCardBrand] = useState('');
  const [isCard, setIsCard] = useState(null);
  const [isExp, setIsExp] = useState(null);
  const [isCvv, setIsCvv] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [showApplePay, setShowApplePay] = useState(false);
  const [showGooglePay, setShowGooglePay] = useState(false);
  const debouncedValidity = useDebounce(isValid, 500);
  const plan = useSelector(selectPlan);
  const token = useSelector(selectToken);
  const eventsData = useSelector(selectEventsData);
  const analyticsParams = useSelector(selectAnalyticsData);
  const userUuid = useSelector(selectUserUuid);
  const isAppleDevice = osName === OsTypes.IOS || osName === OsTypes.MAC_OS;
  const switchFocusToRef = useRef(null);
  const googlePayButton = useRef(null);
  const showCheckout = useSelector(selectShowCheckout)
  const commonTypes = (type) => {
    switch (type) {
      case 'visa':
      case 'Visa': return 'visa';
      case 'Mastercard':
      case 'master-card': return 'mastercard';
      case 'American Express':
      case 'american-express': return 'amex';
      case 'Maestro':
      case 'maestro': return 'discover';
      default: return '';
    }
  };

  useEffect(() => {
    if (cardBrand && eventsData && (eventsData['cardBrand'] !== cardBrand)) {
      dispatch(setEventData({id: 'cardBrand', data: cardBrand}));
      sendAnalyticsEvents(EVENTS.cardMethodChosen, {...eventsData, 'cardBrand': cardBrand});
    }
  }, [cardBrand, eventsData, dispatch]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.onload = () => {
      paymentsClient.current = new window.google.payments.api.PaymentsClient({
        environment: process.env.REACT_APP_ENV === 'stage' ? 'TEST' : 'PRODUCTION'
      });
    };
    document.body.appendChild(script);
  },[]);

  useEffect(() => {
    if (braintreeInstance) {
      if (isAppleDevice) {
        if (window.ApplePaySession?.canMakePayments?.()) {
          braintree.applePay.create(
            {
              client: braintreeInstance,
              // authorization: btToken,
            },
            (error, applePay) => {
              if (error) {
                console.error('Failed to create Apple payment:', error);
                return;
              }
              applePayInstance.current = applePay;
              setShowApplePay(true);
            }
          );
        }
      } else {
        if (paymentsClient.current) {
          braintree.googlePayment.create(
            {
              client: braintreeInstance,
              googlePayVersion: 2,
              googleMerchantId: GOOGLE_PAY_MERCHANT_ID,
            },
            (error, googlePay) => {
              if (error) {
                console.error('Failed to create google pay', error);
                return;
              }
              googlePayInstance.current = googlePay;

              paymentsClient.current.isReadyToPay({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: googlePay.createPaymentDataRequest().allowedPaymentMethods,
                existingPaymentMethodRequired: true
              }).then((isReadyToPay) => {
                if (isReadyToPay.result) {
                  setShowGooglePay(true);

                  const button = paymentsClient.current.createButton({
                    buttonColor: 'black',
                    buttonType: 'checkout',
                    buttonLocale: 'en',
                    buttonSizeMode: 'fill',
                    onClick: () => {},
                  });

                  googlePayButton.current.appendChild(button);
                }
              });
            }
          );
        }
      }
      braintree.hostedFields.create(
        {
          client: braintreeInstance,
          authorization: btToken,
          styles: {
            'input': {
              'font-size': '16px',
              'color': '#323232',
              'line-height': '22px',
              'font-weight': '300',
              'font-family': 'Open Sans, sans-serif',
              'text-align': textAlign,
            },
            'input::placeholder': {
              'font-size': '16px',
              'color': '#909090',
              'line-height': '22px',
              'letter-spacing': '1.2px',
            },
            '.invalid': {
              'color': '#eb1c26',
            },
          },
          fields: {
            number: {
              container: card.current,
              placeholder: '0000 0000 0000 0000',
            },
            expirationDate: {
              container: expiration.current,
              placeholder: t('MM/YY'),
            },
            cvv: {
              container: cvc.current,
              placeholder: t('CVV'),
            },
          },
        },
        (error, hostedFieldsInstance) => {
          if (error) {
            console.error('Error creating card form:', error);
            return;
          }

          const switchFocusTo = (field) => {
            hostedFieldsInstance.focus(field);
          };

          switchFocusToRef.current = switchFocusTo;

          switchFocusTo('number');

          hostedFieldsInstance.on('cardTypeChange', (event) => {
            if (event.cards.length === 1) {
              const type = commonTypes(event.cards[0].type);
              setCardBrand(type);
            } else {
              setCardBrand('');
            }
          });

          hostedFieldsInstance.on('validityChange', (event) => {
            const field = event.fields[event.emittedBy];
            const valid = field.isValid;

            switch (event.emittedBy) {
              case 'number':
                setIsCard(valid);
                valid && switchFocusTo('expirationDate');
                break;
              case 'expirationDate':
                setIsExp(valid);
                valid && switchFocusTo('cvv');
                break;
              case 'cvv':
                setIsCvv(valid);
                valid && cardholder.current.focus();
                break;
              default: return;
            }
          });

          cardInstance.current = hostedFieldsInstance;
        }
      );
      braintree.threeDSecure.create(
        {
          client: braintreeInstance,
          version: '2',
        },
        (error, threeDSecure) => {
          if (error) {
            console.error('Error creating 3DS:', error);
            return;
          }

          threeDSInstance.current = threeDSecure;

          threeDSecure.on('lookup-complete', (data, next) => {
            // console.log('lookup: ', data);
            next();
          });
        }
      );
    }
  },[isAppleDevice, braintreeInstance, cardInstance, threeDSInstance, btToken, card, expiration, cvc, cardholder, t]);

  useEffect(() => {
    const handleFocus = () => {
      if (showCheckout && switchFocusToRef.current) {
        switchFocusToRef.current('number');
      }
    };
    handleFocus();
  }, [showCheckout]);

  useEffect(() => {
    setIsValid(isCard && isCvv && isExp);
  }, [isCard, isCvv, isExp, dispatch]);

  useEffect(() => {
    dispatch(setFormValidity(debouncedValidity));
  }, [debouncedValidity, dispatch]);

  const payViaApple = () => {
    const fullEventsData = {
      ...eventsData,
      payment_method: 'Braintree ApplePay',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    const paymentRequest = applePayInstance.current.createPaymentRequest({
      label: `Family Locator ${t(planDetails?.description)} plan`,
      currency: 'usd',
      total: {
        label: `Family Locator ${t(planDetails?.description)} plan`,
        amount: planDetails.periodPrice.toString()
      },
      displayItems: [
        {
          label: `Family Locator ${t(planDetails?.description)} plan`,
          amount: planDetails.periodPrice.toString(),
        },
      ],
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const session = new window.ApplePaySession(3, paymentRequest);
    session.onvalidatemerchant = function (event) {
      applePayInstance.current.performValidation({
        validationURL: event.validationURL,
        displayName: 'Family Locator'
      }, function (err, merchantSession) {
        if (err) {
          dispatch(setCheckoutLoader({ show: false, type: '' }));
          console.error('Apple Pay failed to load.');
          return;
        }
        session.completeMerchantValidation(merchantSession);
      });
    };

    session.onpaymentauthorized = function (event) {
      // console.log('Your shipping address is:', event.payment.shippingContact);

      applePayInstance.current.tokenize({
        token: event.payment.token
      }, function (tokenizeErr, payload) {
        if (tokenizeErr) {
          dispatch(setCheckoutLoader({ show: false, type: '' }));
          console.error('Error tokenizing Apple Pay:', tokenizeErr);
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          return;
        }

        const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
        const localEventsData = {
          payment_method: 'Braintree ApplePay',
          cardBrand,
          card_brand: cardBrand,
        };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            payload.binData,
            null,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            userUuid,
          )
        );

        // If requested, address information is accessible in event.payment
        // and may also be sent to your server.
        // console.log('billingPostalCode:', event.payment.billingContact.postalCode);

        // After you have transacted with the payload.nonce,
        // call `completePayment` to dismiss the Apple Pay sheet.
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      });
    };

    session.oncancel = () => {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    };

    session.begin();
  };

  const payViaGoogle = () => {
    const fullEventsData = {
      ...eventsData,
      payment_method: 'Braintree GooglePay',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    const paymentDataRequest = googlePayInstance.current.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: 'USD',
        totalPriceStatus: 'FINAL',
        totalPrice: planDetails.periodPrice.toString(),
      }
    });

    paymentsClient.current.loadPaymentData(paymentDataRequest)
      .then((paymentData) => {
        googlePayInstance.current.parseResponse(paymentData, (err, payload) => {
          if (err) {
            dispatch(setCheckoutLoader({ show: false, type: '' }));
            console.error('Failed to parse payment response', err);
            return;
          }

          const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
          const localEventsData = {
            payment_method: 'Braintree GooglePay',
            cardBrand,
            card_brand: cardBrand,
          };
          const extendedEventsData = {
            ...eventsData,
            ...localEventsData,
          };
          setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
          dispatch(setEventDataList(localEventsData));
          dispatch(
            braintreePurchase(
              payload.nonce,
              payload.binData,
              null,
              plan,
              token,
              analyticsParams,
              extendedEventsData,
              userUuid,
            )
          );
        })
      }).catch((err) => {
      console.error('Failed to pay via store', err);
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    });
  };

  return (
    <>
      <CardForm
        method={'braintree'}
        card={card}
        expiration={expiration}
        cardholder={cardholder}
        cvc={cvc}
        cardBrand={cardBrand}
        renderBrand={renderBrand}
      />
      {showApplePay && <ApplePayButton onClick={payViaApple} />}
      {showGooglePay && <GooglePayButton onClick={payViaGoogle} ref={googlePayButton} />}
    </>
  );
};

export default Braintree;
